/**
 * Every piece of prose in the app hides behind one of these. Click the (i) to
 * read it, click anywhere else or press Escape to put it away.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export function Info({
  title,
  children,
  align = 'left',
  more,
}: {
  title: string;
  children: ReactNode;
  align?: 'left' | 'right';
  /**
   * A `<PageLink>` to the long-form page this popover is the summary of. Pinned
   * to the bottom as its own row so it reads as an exit rather than as another
   * sentence — the popover is a reference card, and the page is the argument.
   */
  more?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <span className="info" ref={wrapperRef}>
      <button
        type="button"
        className={`info-btn${open ? ' open' : ''}`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`About: ${title}`}
        onClick={() => setOpen((current) => !current)}
      >
        i
      </button>
      {open ? (
        <span className={`info-panel info-panel-${align}`} id={panelId} role="note">
          <b className="info-title">{title}</b>
          {children}
          {more ? <span className="info-more">{more}</span> : null}
        </span>
      ) : null}
    </span>
  );
}
